import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { currencyUtils } from '@/utils'
import type { FiatAmount } from '@/types'

const { parseAmountToFloat, formatFiatCurrency } = currencyUtils

export interface UseValidateTransactionAmountProps {
  cryptoCode?: string
  min?: FiatAmount
  max?: FiatAmount
  onInvalidAmount?: () => void
  customMinMessage?: string
  customMaxMessage?: string
}

export interface ValidateTransactionAmountProps {
  fiatAmount?: FiatAmount
  isConfirmationScreen?: boolean
}

export const useValidateFiatAmount = ({
  cryptoCode,
  min,
  max,
  onInvalidAmount,
  customMinMessage,
  customMaxMessage,
}: UseValidateTransactionAmountProps) => {
  const { t } = useTranslation()

  const formattedMinFiat = min
    ? formatFiatCurrency({
        fiatCode: min.code || 'USD',
        value: Number(min.amount || 0),
        showDecimals: true,
      })
    : ''

  const formattedMaxFiat = max
    ? formatFiatCurrency({
        fiatCode: max.code || 'USD',
        value: Number(max.amount || 0),
        showDecimals: true,
      })
    : ''

  const minMessage = customMinMessage
    ? t(customMinMessage)
    : `Min. ${cryptoCode} is ${formattedMinFiat}`

  const maxMessage = customMaxMessage
    ? t(customMaxMessage)
    : `Max. ${cryptoCode} is ${formattedMaxFiat}`

  const validateFiatAmount = useCallback(
    ({
      fiatAmount,
      isConfirmationScreen = false,
    }: ValidateTransactionAmountProps): boolean => {
      const amountParseFloat = parseAmountToFloat({
        amount: fiatAmount?.amount || '',
        decimalSeparator: '.',
      })

      // IF VALUE IS LESS OR EQUAL TO 0, TRIGGER SHAKE
      if (amountParseFloat <= 0 && !isConfirmationScreen) {
        onInvalidAmount?.()
        return false
      }

      // IF VALUE IS LESS THAN MIN, SHOW MIN VALUE
      if (min?.amount && amountParseFloat < Number(min.amount)) {
        onInvalidAmount?.()

        toast.error(minMessage, {
          style: {
            width: 'calc(100vw - 26px)',
            marginLeft: '13px',
            marginRight: '13px',
            maxWidth: 'calc(100vw - 26px)',
          },
          icon: '⚠️',
        })
        return false
      }

      // IF VALUE IS GREATER THAN MAX, SHOW MAX VALUE
      if (max?.amount && amountParseFloat > Number(max.amount)) {
        onInvalidAmount?.()

        toast.error(maxMessage, {
          style: {
            width: 'calc(100vw - 26px)',
            marginLeft: '13px',
            marginRight: '13px',
            maxWidth: 'calc(100vw - 26px)',
          },
          icon: '⚠️',
        })
        return false
      }

      return true
    },
    [onInvalidAmount, min, max, maxMessage, minMessage],
  )

  return { validateFiatAmount }
}
